/**
 * @button 模式关面板：声明式重建输入框序列化串（不直接改 DOM，避免 hydrate 竞态）
 */
import type { ISelectedKnowledgeTagData } from '../components/SelectedKnowledgeTag';
import { getKnowledgeTagKey } from '../components/SelectedKnowledgeTag';
import {
  buildKnowledgeInlineLabel,
  mentionLabelMatchVariants,
} from '../../utils/inlineMentionLabel';
import {
  findSerializedMentionRanges,
  MENTION_BOUNDARY_MARKER,
  type IInlineMentionRange,
} from '../../utils/inlineMentionRanges';
import {
  normalizeSerializedMentionGaps,
  parseMentionBodyFromLabel,
} from '../../mentionEditor/mentionTokenDom';

const stripRangesFromValue = (
  value: string,
  ranges: IInlineMentionRange[],
): string => {
  const sorted = [...ranges].sort((a, b) => b.start - a.start);
  let result = value;
  sorted.forEach((range) => {
    result = result.slice(0, range.start) + result.slice(range.end);
  });
  return result;
};

const adjustOffsetAfterRemovingRanges = (
  offset: number,
  removedRanges: IInlineMentionRange[],
): number => {
  let adjusted = offset;
  removedRanges.forEach((range) => {
    if (range.start < offset) {
      adjusted -= range.end - range.start;
    }
  });
  return Math.max(0, adjusted);
};

const groupTagsByKb = (
  tags: ISelectedKnowledgeTagData[],
): ISelectedKnowledgeTagData[][] => {
  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  tags.forEach((item) => {
    const list = byKb.get(item.kbId) || [];
    list.push(item);
    byKb.set(item.kbId, list);
  });
  return Array.from(byKb.values()).filter((group) => group.length > 0);
};

const collectMentionBodies = (label: string): Set<string> => {
  const bodies = new Set<string>();
  mentionLabelMatchVariants(label.trim()).forEach((variant) => {
    const body = parseMentionBodyFromLabel(variant, 'knowledge').trim();
    if (body) bodies.add(body);
  });
  return bodies;
};

const findRangesMatchingBodies = (
  value: string,
  targetBodies: Set<string>,
): IInlineMentionRange[] =>
  findSerializedMentionRanges(value).filter((range) => {
    if (range.kind !== 'knowledge') return false;
    const body = parseMentionBodyFromLabel(range.label, 'knowledge').trim();
    return targetBodies.has(body);
  });

const findRangesMatchingGroup = (
  value: string,
  group: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): IInlineMentionRange[] => {
  const label = buildKnowledgeInlineLabel(group, kbDocUnit).trim();
  if (!label) return [];
  return findRangesMatchingBodies(value, collectMentionBodies(label));
};

/** 同库 mention 定位：先精确 label，再按库名前缀兜底（2篇→3篇 等 label 形态变化） */
const findRangesForKbGroup = (
  value: string,
  group: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): IInlineMentionRange[] => {
  const byLabel = findRangesMatchingGroup(value, group, kbDocUnit);
  if (byLabel.length) return byLabel;

  const kbName = group[0]?.kbName?.trim();
  if (!kbName) return [];

  return findSerializedMentionRanges(value).filter((range) => {
    if (range.kind !== 'knowledge') return false;
    const body = parseMentionBodyFromLabel(range.label, 'knowledge').trim();
    if (!body) return false;
    return (
      body === kbName
      || body.startsWith(`${kbName}-`)
      || body.startsWith(`${kbName} `)
    );
  });
};

const findRangesMatchingTag = (
  value: string,
  tag: ISelectedKnowledgeTagData,
  kbDocUnit: string,
): IInlineMentionRange[] =>
  findRangesMatchingGroup(value, [tag], kbDocUnit);

const mergeRanges = (...rangeLists: IInlineMentionRange[][]): IInlineMentionRange[] => {
  const map = new Map<number, IInlineMentionRange>();
  rangeLists.flat().forEach((range) => {
    map.set(range.start, range);
  });
  return Array.from(map.values());
};

const buildKbLabelWithMarker = (
  group: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): string => {
  const raw = buildKnowledgeInlineLabel(group, kbDocUnit).trim();
  if (!raw) return '';
  return raw.endsWith(MENTION_BOUNDARY_MARKER)
    ? raw
    : `${raw}${MENTION_BOUNDARY_MARKER}`;
};

const insertKbLabelsAtOffset = (
  value: string,
  offset: number,
  insertText: string,
): string => {
  if (!insertText) return value;

  const clampedOffset = Math.min(Math.max(0, offset), value.length);
  const prevChar = clampedOffset > 0 ? value[clampedOffset - 1] : '';
  const nextChar = clampedOffset < value.length ? value[clampedOffset] : '';

  const needsLeadingSpace =
    clampedOffset > 0
    && prevChar
    && !/\s/.test(prevChar);
  const needsTrailingSpace =
    clampedOffset < value.length
    && nextChar
    && !/\s/.test(nextChar);

  const prefix = needsLeadingSpace ? ' ' : '';
  const suffix = needsTrailingSpace ? ' ' : '';

  return (
    value.slice(0, clampedOffset)
    + prefix
    + insertText
    + suffix
    + value.slice(clampedOffset)
  );
};

const sameTagKeys = (
  a: ISelectedKnowledgeTagData[],
  b: ISelectedKnowledgeTagData[],
): boolean => {
  const keysA = new Set(a.map((item) => getKnowledgeTagKey(item)));
  const keysB = new Set(b.map((item) => getKnowledgeTagKey(item)));
  if (keysA.size !== keysB.size) return false;
  return [...keysA].every((key) => keysB.has(key));
};

/** 同库选区变化时，将正文中的旧分组 mention 更新为新 label（如 2篇→3篇、3篇→2篇） */
const replaceGroupMentionsForKb = (
  value: string,
  prevGroup: ISelectedKnowledgeTagData[],
  nextGroup: ISelectedKnowledgeTagData[],
  kbDocUnit: string,
): string => {
  const newSerialized = buildKbLabelWithMarker(nextGroup, kbDocUnit);
  if (
    !newSerialized
    || buildKnowledgeInlineLabel(prevGroup, kbDocUnit).trim()
      === buildKnowledgeInlineLabel(nextGroup, kbDocUnit).trim()
  ) {
    return value;
  }

  const ranges = findRangesForKbGroup(value, prevGroup, kbDocUnit);
  if (!ranges.length) return value;

  let result = value;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const [primary, ...duplicates] = sorted;

  duplicates
    .sort((a, b) => b.start - a.start)
    .forEach((range) => {
      result = result.slice(0, range.start) + result.slice(range.end);
    });

  result = (
    result.slice(0, primary.start)
    + newSerialized
    + result.slice(primary.end)
  );
  return result;
};

/**
 * 根据 @button 面板勾选结果生成新的 inputValue：
 * - 全清：移除全部知识库 mention
 * - 取消勾选：仅移除对应 tag / 整库分组 mention
 * - 同库选区变化：更新分组 mention（不整段删除）
 * - 仅插入本次新增的知识库 mention
 */
export const buildKbButtonPanelSerializedValue = (
  currentValue: string,
  groups: ISelectedKnowledgeTagData[][],
  kbDocUnit: string,
  insertOffset: number,
  previousSelected: ISelectedKnowledgeTagData[] = [],
): string => {
  const nextTags = groups.flat();
  let value = currentValue || '';

  if (!nextTags.length) {
    const allKbRanges = findSerializedMentionRanges(value).filter(
      (range) => range.kind === 'knowledge',
    );
    return normalizeSerializedMentionGaps(stripRangesFromValue(value, allKbRanges));
  }

  const prevKeys = new Set(previousSelected.map((item) => getKnowledgeTagKey(item)));
  const nextKeys = new Set(nextTags.map((item) => getKnowledgeTagKey(item)));
  const removedTags = previousSelected.filter(
    (item) => !nextKeys.has(getKnowledgeTagKey(item)),
  );
  const prevKbIds = new Set(previousSelected.map((item) => item.kbId));
  const addedTags = nextTags.filter(
    (item) => !prevKeys.has(getKnowledgeTagKey(item)),
  );
  /** 同库扩选只走 replace，禁止再 insert（否则会与更新后的 mention 重复） */
  const addedTagsForNewKb = addedTags.filter(
    (item) => !prevKbIds.has(item.kbId),
  );

  const removedRanges: IInlineMentionRange[] = [];
  const nextKbIds = new Set(nextTags.map((item) => item.kbId));

  removedTags.forEach((tag) => {
    // 同库仍有勾选：正文 mention 交给下方 replace 更新，勿先按单篇 strip。
    // 否则「舆情→8888」会删光旧串，且同库不允许 insert，输入框变空、面板仍勾选。
    if (nextKbIds.has(tag.kbId)) return;
    removedRanges.push(...findRangesMatchingTag(value, tag, kbDocUnit));
  });

  groupTagsByKb(previousSelected).forEach((prevGroup) => {
    const kbId = prevGroup[0]?.kbId;
    if (!kbId || nextKbIds.has(kbId)) return;
    removedRanges.push(...findRangesMatchingGroup(value, prevGroup, kbDocUnit));
  });

  const safeOffset = adjustOffsetAfterRemovingRanges(insertOffset, removedRanges);
  value = stripRangesFromValue(value, mergeRanges(removedRanges));

  groupTagsByKb(nextTags).forEach((nextGroup) => {
    const prevGroup = groupTagsByKb(previousSelected).find(
      (group) => group[0]?.kbId === nextGroup[0]?.kbId,
    );
    if (!prevGroup || sameTagKeys(prevGroup, nextGroup)) return;
    value = replaceGroupMentionsForKb(value, prevGroup, nextGroup, kbDocUnit);
  });

  if (!addedTagsForNewKb.length) {
    return normalizeSerializedMentionGaps(value);
  }

  const labels = groupTagsByKb(addedTagsForNewKb)
    .map((group) => buildKbLabelWithMarker(group, kbDocUnit))
    .filter(Boolean);

  if (!labels.length) {
    return normalizeSerializedMentionGaps(value);
  }

  return normalizeSerializedMentionGaps(
    insertKbLabelsAtOffset(value, safeOffset, labels.join(' ')),
  );
};
